export type RobotChannel = 'location' | 'telemetry' | 'lidar' | 'alert' | 'command'

export const ROBOT_CHANNELS: RobotChannel[] = ['location', 'telemetry', 'lidar', 'alert', 'command']
// robot→subscribers up-topics (published by the owning robot, fanned to operators;
// lidar is fanned WITHOUT persistence, exactly like telemetry).
export const ROBOT_UP_CHANNELS: RobotChannel[] = ['location', 'telemetry', 'lidar', 'alert']

export type TopicKind = 'robot' | 'fleet'

export type ParsedTopic =
  | { kind: 'robot'; robotId: string; channel: RobotChannel }
  | { kind: 'fleet'; tenant: string | null }

/**
 * The reserved label for the CENTRAL fleet scope (`fleet:central`). Central
 * operators + central robots both carry `tenant === null`; the wire topic needs
 * a concrete label, and `central` is already the codebase-wide sentinel for the
 * null tenant — `readClaimTenant` maps the claim `'central'` → null, signup
 * reserves it, and `PgPoolRegistry` keys null pools as `'central'`. So a real
 * tenant can never be named `central`, and `fleet:central` can never collide
 * with a `fleet:{tenant}` topic.
 */
export const CENTRAL_FLEET_LABEL = 'central'

export function parseTopic(topic: string): ParsedTopic | null {
  const parts = topic.split(':')

  if (parts[0] === 'robot' && parts.length === 3) {
    const [, robotId, channel] = parts

    if (robotId && (ROBOT_CHANNELS as string[]).includes(channel ?? '')) {
      return { kind: 'robot', robotId, channel: channel as RobotChannel }
    }

    return null
  }

  if (parts[0] === 'fleet' && parts.length === 2 && parts[1]) {
    return { kind: 'fleet', tenant: parts[1] === CENTRAL_FLEET_LABEL ? null : parts[1] }
  }

  return null
}

export const robotTopic = (robotId: string, channel: RobotChannel): string => `robot:${robotId}:${channel}`

/** Fleet roll-up topic. `null` (central) → `fleet:central`; a tenant → `fleet:{tenant}`. */
export const fleetTopic = (tenant: string | null): string => `fleet:${tenant ?? CENTRAL_FLEET_LABEL}`
